import { ANY_RESOURCE_ID } from '../../constants'
import { AvailabilitiesResponse } from '../../types'
import { MergedAvailabilities } from '../../types/mergedAvailabilities'

/**
 * Transforms availability data from a nested resource-based structure to a flat array structure.
 *
 * Input structure: dayAvailability[date][resourceId] = TimeBlock[]
 * Output structure: dayAvailability[date] = ResourceAvailability[]
 *
 * Key behaviors:
 * - Flattens the nested structure for easier UI consumption
 * - Merges slots from the same specialist with different cabins (extraResourceIds)
 * - Keeps slots from different specialists separate (even if at the same time)
 * - Filters out resources without calendar integration
 * - Sorts slots chronologically within each day
 *
 * @param availabilities - Raw availability response from the API
 * @returns Merged availability data organized by date with flattened slot arrays
 */
const mergeAvailabilities = (availabilities?: AvailabilitiesResponse): MergedAvailabilities => {
  const mergedAvailabilities: MergedAvailabilities = {}

  Object.keys(availabilities?.dayAvailability ?? {}).forEach((date) => {
    const daySlots: MergedAvailabilities[string] = []

    // Maps composite key (date_start:resourceId) to index in daySlots
    // This allows us to merge slots from the same specialist while keeping different specialists separate
    const slotMap = new Map<string, number>()

    Object.keys(availabilities?.dayAvailability[date] ?? {}).forEach((resourceId) => {
      // Skip ANY_RESOURCE_ID ('*') - it's a wildcard used for "Anyone" filtering in the UI
      // and shouldn't be included in the merged structure
      if (resourceId === ANY_RESOURCE_ID) return

      // Only include resources with calendar integration - resources without integration
      // can't actually be booked, so we filter them out here
      if (availabilities?.resourceHasCalendarIntegration[resourceId]) {
        availabilities?.dayAvailability[date][resourceId].forEach((block) => {
          // Use composite key: date_start + primary resourceId (specialist/fitter)
          // This ensures:
          // - Different specialists with the same time slot are kept as separate entries
          // - Same specialist with different cabins at the same time get merged into one entry
          const slotKey = `${block.date_start.toISOString()}:${resourceId}`
          const existingIndex = slotMap.get(slotKey)

          if (existingIndex !== undefined) {
            // Slot already exists for this specialist - merge extraResourceIds (cabins)
            // This handles cases where the same specialist has multiple blocks at the same time
            // with different cabin assignments (e.g., from filterSlotsByResourceTypes)
            const existingSlot = daySlots[existingIndex]
            const newExtraResourceIds = block.extraResourceIds || []

            // Add cabin IDs that aren't already present in the resource_ids array
            newExtraResourceIds.forEach((id) => {
              if (!existingSlot.resource_ids.includes(id)) {
                existingSlot.resource_ids.push(id)
              }
            })
          } else {
            // New slot for this specialist - create a new entry
            const index = daySlots.length
            slotMap.set(slotKey, index)
            daySlots.push({
              date_end: block.date_end,
              date_start: block.date_start,
              resource_ids: [resourceId].concat(block.extraResourceIds || []),
            })
          }
        })
      }
    })

    daySlots.sort((a, b) => a.date_start.getTime() - b.date_start.getTime())

    mergedAvailabilities[date] = daySlots
  })

  return mergedAvailabilities
}

export { mergeAvailabilities }
